import type { ReactNode, SVGProps } from 'react';

type IconProps = { size?: number } & Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>;

function makeIcon(children: ReactNode) {
  return function ShellIcon({ size = 16, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...rest}
      >
        {children}
      </svg>
    );
  };
}

// Glyphs drawn to the design handoff (export-scan-flow) where lucide's differ.
export const DocsIcon = makeIcon(
  <>
    <path d="M4 19V5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
    <path d="M8 7h7" />
  </>,
);

export const SupportIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m14.5 9.5 3-3M6.5 17.5l3-3M14.5 14.5l3 3M6.5 6.5l3 3" />
  </>,
);

export const OverviewIcon = makeIcon(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </>,
);

export const AllServicesIcon = makeIcon(
  <>
    <path d="m12 3 9 5-9 5-9-5z" />
    <path d="m3 13 9 5 9-5" />
  </>,
);

export const SecurityIcon = makeIcon(<path d="M12 3l7 3v6c0 4.4-2.9 8.3-7 9.5C7.9 20.3 5 16.4 5 12V6z" />);

export const KubernetesIcon = makeIcon(
  <>
    <path d="m12 3 7.5 4.3v9.4L12 21l-7.5-4.3V7.3z" />
    <circle cx="12" cy="12" r="2.5" />
  </>,
);

export const ManagementIcon = makeIcon(
  <>
    <path d="M4 7h16M4 12h16M4 17h16" />
    <circle cx="9" cy="7" r="2" fill="var(--vn-bg-surface)" />
    <circle cx="15" cy="12" r="2" fill="var(--vn-bg-surface)" />
    <circle cx="8" cy="17" r="2" fill="var(--vn-bg-surface)" />
  </>,
);

export const AccountIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="10" r="3" />
    <path d="M6.5 19a6 6 0 0 1 11 0" />
  </>,
);

export const SidebarToggleIcon = makeIcon(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16" />
  </>,
);

export const SearchIcon = makeIcon(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </>,
);

export const FolderIcon = makeIcon(
  <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
);

export const MoonIcon = makeIcon(<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5" />);

export const SunIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </>,
);

export const KeyIcon = makeIcon(
  <>
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="m21 2-9.6 9.6" />
    <path d="m15.5 7.5 3 3L22 7l-3-3" />
  </>,
);

export const SignOutIcon = makeIcon(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </>,
);

export const ComputeIcon = makeIcon(
  <>
    <rect x="3" y="4" width="18" height="7" rx="2" />
    <rect x="3" y="13" width="18" height="7" rx="2" />
  </>,
);

export const DatabaseIcon = makeIcon(
  <>
    <ellipse cx="12" cy="5.5" rx="7.5" ry="3" />
    <path d="M4.5 5.5v13c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-13" />
    <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
  </>,
);

export const StorageIcon = makeIcon(
  <>
    <rect x="3" y="9" width="18" height="10" rx="2" />
    <path d="m5.5 9 2-4h9l2 4" />
    <path d="M7 14h.01M11 14h.01" />
  </>,
);

export const StreamingIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m10 8.5 5.5 3.5L10 15.5z" />
  </>,
);

export const GlobeIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9s1.3-6.4 3.8-9z" />
  </>,
);

export const ObservabilityIcon = makeIcon(<path d="M3 12h4l2.5-7 4 14 2.5-7h5" />);

export const MailIcon = makeIcon(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </>,
);

export const HelpIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.2A2.6 2.6 0 0 1 12 7.4c1.4 0 2.5 1 2.5 2.3 0 1.9-2.5 1.8-2.5 3.6" />
    <path d="M12 17h.01" />
  </>,
);

export const ArrowRightIcon = makeIcon(<path d="M4 12h15M13 6l6 6-6 6" />);

export const ChevronRightIcon = makeIcon(<path d="m9 5 7 7-7 7" />);

export const ChevronLeftIcon = makeIcon(<path d="m15 6-6 6 6 6" />);

export const ShieldCheckIcon = makeIcon(
  <>
    <path d="M12 3l7 3v6c0 4.4-2.9 8.3-7 9.5C7.9 20.3 5 16.4 5 12V6z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);

export const SupportMailIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m14.5 9.5 3-3M6.5 17.5l3-3" />
  </>,
);
